/**
 * Login-method glyphs shown beneath a Zoom user's email — rendered the way Zoom itself shows them:
 * the real BRAND marks (multi-colour Google "G", Microsoft's four squares, the Apple/Facebook logos,
 * an envelope tile for work email, an "SSO" pill) rather than plain letter circles.
 *
 * Zoom `login_type` codes: 100 = Work email · 101 = SSO · 1 = Google · 0 = Facebook · 24 = Apple ·
 * 27 = Microsoft · 11 = Phone. A user can have several; we de-dupe and render one mark each.
 */

import { Mail, Phone } from "lucide-react";

const SIZE = "size-4"; // ~16px, matching the email tile + SSO pill height

function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className={SIZE} role="img" aria-label="Google" focusable="false">
      <title>Google</title>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18A10.98 10.98 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.83z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"
      />
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg viewBox="0 0 24 24" className={SIZE} role="img" aria-label="Microsoft" focusable="false">
      <title>Microsoft</title>
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="13" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

function AppleMark() {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`${SIZE} text-(--text-strong)`}
      fill="currentColor"
      role="img"
      aria-label="Apple"
      focusable="false"
    >
      <title>Apple</title>
      <path d="M17.05 12.04c-.03-2.6 2.12-3.85 2.22-3.91-1.21-1.77-3.1-2.01-3.77-2.04-1.6-.16-3.13.94-3.94.94-.81 0-2.07-.92-3.4-.9-1.75.03-3.36 1.02-4.26 2.58-1.82 3.16-.47 7.82 1.3 10.38.86 1.25 1.89 2.66 3.24 2.61 1.3-.05 1.79-.84 3.36-.84 1.57 0 2.01.84 3.39.81 1.4-.03 2.28-1.28 3.14-2.54.99-1.45 1.4-2.86 1.42-2.93-.03-.01-2.72-1.05-2.75-4.13zM14.6 4.7c.72-.87 1.2-2.08 1.07-3.28-1.03.04-2.28.69-3.02 1.56-.66.77-1.24 2-1.09 3.18 1.15.09 2.32-.59 3.04-1.46z" />
    </svg>
  );
}

function FacebookMark() {
  return (
    <svg viewBox="0 0 24 24" className={SIZE} role="img" aria-label="Facebook" focusable="false">
      <title>Facebook</title>
      <path
        fill="#1877F2"
        d="M24 12c0-6.63-5.37-12-12-12S0 5.37 0 12c0 5.99 4.39 10.95 10.13 11.85v-8.38H7.08V12h3.05V9.41c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.95.93-1.95 1.87V12h3.32l-.53 3.47h-2.79v8.38C19.61 22.95 24 17.99 24 12z"
      />
    </svg>
  );
}

/** Work email — a dark rounded tile with a white envelope, mirroring Zoom's email glyph. */
function EmailTile() {
  return (
    <span
      title="Work email"
      role="img"
      aria-label="Work email"
      className="grid size-4 place-items-center rounded-[4px] bg-(--text-strong)"
    >
      <Mail className="size-2.5 text-(--text-onbrand)" aria-hidden />
    </span>
  );
}

/** SSO — a compact text pill (Zoom shows the literal "SSO"). */
function SsoPill() {
  return (
    <span
      title="Single Sign-On"
      role="img"
      aria-label="SSO"
      className="grid h-4 min-w-4 place-items-center rounded-[4px] bg-(--text-strong) px-1 text-[0.5rem] font-bold leading-none text-(--text-onbrand)"
    >
      SSO
    </span>
  );
}

export function LoginTypeIcon({ type }: { type: number }) {
  switch (type) {
    case 100:
      return <EmailTile />;
    case 101:
      return <SsoPill />;
    case 1:
      return <GoogleMark />;
    case 0:
      return <FacebookMark />;
    case 27:
      return <MicrosoftMark />;
    case 24:
      return <AppleMark />;
    case 11:
      return <Phone className="size-4 text-(--text-muted)" aria-label="Phone" />;
    default:
      return <Mail className="size-4 text-(--text-subtle)" aria-label="Login" />;
  }
}

/** One brand mark per distinct login method (a user can sign in several ways). */
export function LoginTypeIcons({ types }: { types: number[] | null }) {
  const list = [...new Set(types ?? [])];
  if (list.length === 0) return null;
  return (
    <span className="flex items-center gap-1.5">
      {list.map((x) => (
        <LoginTypeIcon key={x} type={x} />
      ))}
    </span>
  );
}
